import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { api, setSeo, formatApiErrorDetail } from "@/lib/api";
import { STORES, SITE } from "@/data/site";

export default function Contact() {
  useEffect(() => setSeo("Contact | Adam's The Islamic Boutique Chennai & Bengaluru", "Get in touch with Adam's The Islamic Boutique. Visit our stores in Chennai & Bengaluru or reach us online."), []);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 lg:py-12">
      <div className="mb-10 text-center">
        <p className="overline mb-2">We'd Love to Hear From You</p>
        <h1 className="font-serif text-4xl lg:text-6xl text-espresso">Contact Us</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div><label className="text-xs uppercase tracking-wider text-espresso/60 mb-1.5 block">Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-espresso/20 rounded-xl px-4 py-3 bg-ivory outline-none focus:border-champagne text-sm" data-testid="contact-name" /></div>
            <div><label className="text-xs uppercase tracking-wider text-espresso/60 mb-1.5 block">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-espresso/20 rounded-xl px-4 py-3 bg-ivory outline-none focus:border-champagne text-sm" data-testid="contact-email" /></div>
            <div><label className="text-xs uppercase tracking-wider text-espresso/60 mb-1.5 block">Message</label>
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-espresso/20 rounded-xl px-4 py-3 bg-ivory outline-none focus:border-champagne text-sm resize-none" data-testid="contact-message" /></div>
            <button type="submit" className="bg-espresso text-ivory px-10 py-3.5 rounded-full text-sm font-medium hover:bg-espresso/90" data-testid="contact-submit">{sent ? "Message Sent ✓" : "Send Message"}</button>
          </form>

          <div className="mt-10 space-y-4 text-sm">
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-espresso/70 hover:text-champagne"><Mail className="w-5 h-5 text-champagne" /> {SITE.email}</a>
            <a href={`tel:${SITE.phone}`} className="flex items-center gap-3 text-espresso/70 hover:text-champagne"><Phone className="w-5 h-5 text-champagne" /> {SITE.phone}</a>
            <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-espresso/70 hover:text-champagne">
              <svg viewBox="0 0 32 32" className="w-5 h-5 fill-champagne"><path d="M16 3C9.4 3 4 8.4 4 15c0 2.1.6 4.1 1.6 5.9L4 29l8.3-1.6c1.7.9 3.7 1.4 5.7 1.4 6.6 0 12-5.4 12-12S22.6 3 16 3z"/></svg>
              WhatsApp: {SITE.whatsappDisplay}
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-fit">
          {STORES.map((s) => (
            <a key={s.name} href={s.maps} target="_blank" rel="noreferrer" className="bg-beige/50 rounded-2xl p-6 hover:bg-beige transition-colors group" data-testid={`contact-store-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <MapPin className="w-5 h-5 text-champagne mb-3" />
              <h3 className="font-serif text-xl text-espresso">{s.name}</h3>
              <p className="text-xs text-champagne uppercase tracking-wider">{s.city}</p>
              <p className="text-sm text-espresso/60 mt-3 leading-relaxed">{s.address}</p>
              <p className="flex items-center gap-1.5 text-xs text-espresso/50 mt-3"><Clock className="w-3.5 h-3.5" /> {s.hours}</p>
              {s.phone && <p className="flex items-center gap-1.5 text-xs text-espresso/50 mt-1"><Phone className="w-3.5 h-3.5" /> {s.phone}</p>}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
